/**
 * Tiny synthesizer on Web Audio: oscillators/noise with ADSR, filter, pan, and a reverb send.
 * Everything the game plays is described by a SoundDef; nothing is loaded from disk.
 */
export type OscType = 'sine' | 'triangle' | 'sawtooth' | 'square' | 'noise';

export interface SoundDef {
  osc: OscType;
  /** Hz, or [start, end] for a glide over the note. */
  freq: number | [number, number];
  dur: number;
  attack?: number;
  decay?: number;
  sustain?: number; // 0..1 level after decay
  release?: number;
  gain?: number;
  filter?: { type: BiquadFilterType; freq: number | [number, number]; q?: number };
  pan?: number;
  reverb?: number; // 0..1 send
  detune?: number; // cents
  /** Glide curve: 'exp' (default) or 'lin' */
  glide?: 'exp' | 'lin';
  /** Vibrato depth (Hz) and rate. */
  vibrato?: { depth: number; rate: number };
}

export interface AmbientLayer {
  osc: OscType;
  freq: number;
  filter?: { type: BiquadFilterType; freq: number; q?: number };
  gain: number;
  /** Slow amplitude wobble. */
  lfo?: { rate: number; depth: number };
}

export class Synth {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  private readonly reverb: ConvolverNode;
  private readonly reverbGain: GainNode;
  private noiseBuf: AudioBuffer | null = null;
  private readonly ambients = new Map<string, { nodes: AudioNode[]; gain: GainNode; target: number }>();
  volume = 0.8;
  muted = false;

  constructor() {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
    this.reverb = this.ctx.createConvolver();
    this.reverb.buffer = this.impulse(2.2, 2.5);
    this.reverbGain = this.ctx.createGain();
    this.reverbGain.gain.value = 0.35;
    this.reverb.connect(this.reverbGain);
    this.reverbGain.connect(this.master);
  }

  /** Browsers require a gesture; call on first click/keypress. */
  resume(): void { if (this.ctx.state !== 'running') void this.ctx.resume(); }
  setVolume(v: number): void { this.volume = v; this.master.gain.setTargetAtTime(this.muted ? 0 : v, this.ctx.currentTime, 0.05); }
  setMuted(m: boolean): void { this.muted = m; this.setVolume(this.volume); }
  get now(): number { return this.ctx.currentTime; }

  private impulse(seconds: number, decay: number): AudioBuffer {
    const rate = this.ctx.sampleRate, len = Math.floor(rate * seconds);
    const buf = this.ctx.createBuffer(2, len, rate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return buf;
  }

  private noise(): AudioBuffer {
    if (this.noiseBuf) return this.noiseBuf;
    const rate = this.ctx.sampleRate, len = rate * 2;
    const buf = this.ctx.createBuffer(1, len, rate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return (this.noiseBuf = buf);
  }

  private source(osc: OscType, freq: number | [number, number], dur: number, t0: number, glide: 'exp' | 'lin', detune = 0, vib?: { depth: number; rate: number }): AudioScheduledSourceNode {
    if (osc === 'noise') {
      const s = this.ctx.createBufferSource();
      s.buffer = this.noise(); s.loop = true;
      return s;
    }
    const o = this.ctx.createOscillator();
    o.type = osc;
    o.detune.value = detune;
    const f0 = Array.isArray(freq) ? freq[0] : freq;
    o.frequency.setValueAtTime(f0, t0);
    if (Array.isArray(freq)) {
      if (glide === 'exp') o.frequency.exponentialRampToValueAtTime(Math.max(1, freq[1]), t0 + dur);
      else o.frequency.linearRampToValueAtTime(freq[1], t0 + dur);
    }
    if (vib) {
      const l = this.ctx.createOscillator(), lg = this.ctx.createGain();
      l.frequency.value = vib.rate; lg.gain.value = vib.depth;
      l.connect(lg); lg.connect(o.frequency); l.start(t0); l.stop(t0 + dur + 1);
    }
    return o;
  }

  /** Play a sound now (or at `when` seconds from now). Returns the stop time. */
  play(def: SoundDef, when = 0, gainMul = 1, pan?: number): number {
    if (this.muted) return 0;
    const t0 = this.ctx.currentTime + when;
    const a = def.attack ?? 0.005, d = def.decay ?? 0.05, s = def.sustain ?? 0.6, r = def.release ?? 0.08;
    const dur = def.dur;
    const src = this.source(def.osc, def.freq, dur, t0, def.glide ?? 'exp', def.detune, def.vibrato);
    const env = this.ctx.createGain();
    const g = (def.gain ?? 0.5) * gainMul;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(g, t0 + a);
    env.gain.linearRampToValueAtTime(g * s, t0 + a + d);
    env.gain.setValueAtTime(g * s, t0 + Math.max(a + d, dur));
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + Math.max(a + d, dur) + r);
    let node: AudioNode = src;
    if (def.filter) {
      const f = this.ctx.createBiquadFilter();
      f.type = def.filter.type;
      const ff = def.filter.freq;
      f.frequency.setValueAtTime(Array.isArray(ff) ? ff[0] : ff, t0);
      if (Array.isArray(ff)) f.frequency.exponentialRampToValueAtTime(Math.max(10, ff[1]), t0 + dur);
      f.Q.value = def.filter.q ?? 1;
      node.connect(f); node = f;
    }
    node.connect(env);
    let out: AudioNode = env;
    const p = pan ?? def.pan;
    if (p !== undefined && p !== 0) {
      const pn = this.ctx.createStereoPanner();
      pn.pan.value = Math.max(-1, Math.min(1, p));
      env.connect(pn); out = pn;
    }
    out.connect(this.master);
    if (def.reverb) {
      const send = this.ctx.createGain();
      send.gain.value = def.reverb;
      out.connect(send); send.connect(this.reverb);
    }
    const stop = t0 + Math.max(a + d, dur) + r + 0.05;
    src.start(t0);
    src.stop(stop);
    return stop;
  }

  /** Start (or retarget) a named looping ambient bed built from layers. Gains crossfade over `fade` s. */
  ambient(name: string, layers: AmbientLayer[] | null, level: number, fade = 2): void {
    let a = this.ambients.get(name);
    if (!a && layers) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.master);
      const nodes: AudioNode[] = [];
      for (const L of layers) {
        const src = this.source(L.osc, L.freq, 1, this.ctx.currentTime, 'lin');
        const g = this.ctx.createGain();
        g.gain.value = L.gain;
        let n: AudioNode = src;
        if (L.filter) {
          const f = this.ctx.createBiquadFilter();
          f.type = L.filter.type; f.frequency.value = L.filter.freq; f.Q.value = L.filter.q ?? 1;
          n.connect(f); n = f;
        }
        n.connect(g); g.connect(gain);
        if (L.lfo) {
          const l = this.ctx.createOscillator(), lg = this.ctx.createGain();
          l.frequency.value = L.lfo.rate; lg.gain.value = L.gain * L.lfo.depth;
          l.connect(lg); lg.connect(g.gain); l.start();
          nodes.push(l);
        }
        src.start();
        nodes.push(src, g);
      }
      a = { nodes, gain, target: 0 };
      this.ambients.set(name, a);
    }
    if (!a) return;
    a.target = level;
    a.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    a.gain.gain.setTargetAtTime(level, this.ctx.currentTime, fade / 3);
  }

  private readonly voices = new Map<string, { osc: OscillatorNode; gain: GainNode; filter: BiquadFilterNode }>();
  /** A continuously running tone whose pitch/level are steered each frame (magnet hum). level 0 = silent. */
  voice(name: string, osc: Exclude<OscType, 'noise'>, freq: number, level: number, filterFreq = 1200): void {
    let v = this.voices.get(name);
    if (!v) {
      const o = this.ctx.createOscillator(); o.type = osc; o.frequency.value = freq;
      const f = this.ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = filterFreq;
      const g = this.ctx.createGain(); g.gain.value = 0;
      o.connect(f); f.connect(g); g.connect(this.master); o.start();
      v = { osc: o, gain: g, filter: f };
      this.voices.set(name, v);
    }
    const t = this.ctx.currentTime;
    v.osc.frequency.setTargetAtTime(freq, t, 0.05);
    v.filter.frequency.setTargetAtTime(filterFreq, t, 0.05);
    v.gain.gain.setTargetAtTime(this.muted ? 0 : level, t, 0.06);
  }

  stopAllAmbient(fade = 1): void {
    for (const k of this.ambients.keys()) this.ambient(k, null, 0, fade);
  }
}
